import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function Iso27001A832EvidencePage() {
  const faqs = [
    {
      question: 'What is ISO 27001 A.8.32?',
      answer:
        'ISO 27001 Annex A control A.8.32 (Change Management) requires that changes to information processing facilities and systems are managed through a formal procedure covering authorization, documentation, risk assessment, testing, review, and rollback planning. For software teams, GitHub pull requests, branch protection rules, and CI pipelines are the primary mechanisms for satisfying this control.',
    },
    {
      question: 'How does GitHub satisfy ISO 27001 A.8.32?',
      answer:
        'GitHub pull requests provide the change request and authorization record. Branch protection rules enforce review requirements and prevent unauthorized merges. Required status checks provide the testing evidence. Merge commits provide the change record. EvidentTrail captures all of this as structured, exportable evidence mapped to A.8.32\'s nine components.',
    },
    {
      question: 'What evidence does an auditor expect for A.8.32?',
      answer:
        'Auditors typically want to see: documented change requests with authorization records, evidence of review before deployment, testing or CI check results, approval identity and timestamps, configuration snapshots for infrastructure changes, and exception records for any bypassed controls. EvidentTrail exports all of this in PDF, CSV, and JSON formats with SHA-256 checksums.',
    },
    {
      question: 'Does EvidentTrail certify ISO 27001 compliance?',
      answer:
        'No. EvidentTrail generates evidence packs that support ISO 27001 audit reviews. Certification requires an accredited third-party auditor (ISO 27001 lead auditor or certification body) to assess the evidence and issue the certificate.',
    },
    {
      question: 'What is the difference between A.8.32 in ISO 27001:2022 and A.12.1.2 in ISO 27001:2013?',
      answer:
        'ISO 27001:2022 consolidated and renumbered Annex A controls. The change management control previously numbered A.12.1.2 (Change Management) in the 2013 version is now A.8.32 in the 2022 version. The substance is similar — documented, authorized, and tested changes — but A.8.32 reflects updated guidance and aligns with ISO 27002:2022.',
    },
    {
      question: 'How does ISO 27001 A.8.32 compare to SOC 2 CC8.1?',
      answer:
        'Both controls require authorized, reviewed, and documented software changes. SOC 2 CC8.1 is US-focused and assessed by CPA firms. ISO 27001 A.8.32 is the international equivalent recognized globally. The GitHub evidence that satisfies one largely satisfies the other — PR approvals, CI checks, branch protection — making EvidentTrail efficient for teams pursuing both frameworks simultaneously.',
    },
  ];

  const longformSections = [
    {
      title: 'What A.8.32 actually requires',
      paragraphs: [
        'ISO 27001 Annex A control A.8.32 defines nine components that a formal change management procedure must address: change planning and authorization, impact and risk assessment, testing and quality checks, approval before implementation, communication of the change, rollback procedures, record-keeping, and post-implementation review.',
        'For software teams, these requirements translate directly to the code review workflow. The pull request is the formal change request. Branch protection rules define the authorization gate — who must approve and what checks must pass before a merge is allowed. CI pipelines provide the testing and quality evidence. The merge commit is the implementation record.',
        'Auditors assess whether this process operated consistently across the audit period, not just whether it was documented. This is why continuous, automated evidence collection is critical for ISO 27001 A.8.32 — point-in-time screenshots taken at audit time do not demonstrate that controls operated throughout the year.',
      ],
      bullets: [
        'Authorization: Pull request creation and linked approval records',
        'Testing: Required CI check results captured with timestamps',
        'Review: Reviewer identity, review timestamp, and approval decision',
        'Record-keeping: Immutable evidence pack with SHA-256 checksum',
        'Exception handling: Emergency merge records with lifecycle tracking',
      ],
    },
    {
      title: 'Why GitHub evidence alone is not enough',
      paragraphs: [
        'Pull request history in GitHub is not structured for auditor consumption. Auditors performing ISO 27001 A.8.32 assessments need to sample changes across the full audit period — typically 12 months — and verify that each sampled change shows authorization, review, and testing evidence in a single view.',
        'Exporting this manually means screenshots, spreadsheets, and hours spent correlating PR data with CI results and branch protection configurations. The configuration at time of change is often lost because GitHub only shows the current state, not historical branch protection settings.',
        'EvidentTrail solves this by capturing evidence at the moment each change occurs — including the branch protection configuration snapshot that was in effect when the PR was reviewed — and storing it in a structured format ready for auditor sampling at any time.',
      ],
      bullets: [
        'Branch protection settings are not historically tracked by GitHub — EvidentTrail captures snapshots at event time',
        'Manual evidence collection averages 20–40 hours per audit cycle',
        'Screenshots lack the metadata structure auditors need for efficient sampling',
      ],
    },
    {
      title: 'How EvidentTrail maps to A.8.32',
      paragraphs: [
        'EvidentTrail connects to GitHub via a read-only App installation and begins capturing change control events immediately. No repository write access is required, and no source code is stored — only the metadata that constitutes change control evidence.',
        'Each pull request generates an evidence record that includes the PR identifier and description, the author, all reviewer identities and their approval timestamps, the required CI check results (pass/fail with timestamps), the branch protection ruleset that was active at time of review, and the merge commit details. This single record maps to all of the authorization, review, and testing components of A.8.32.',
        'Branch protection drift events — changes to the protection rules themselves — are captured separately as configuration change records with before/after snapshots and severity classification. This addresses the infrastructure change component of A.8.32 that pure PR monitoring misses.',
      ],
      bullets: [
        'Pull request records: authorization, review, testing, and implementation in one artifact',
        'Branch protection snapshots: configuration evidence at time of each change event',
        'Drift detection: records unauthorized changes to protection rules with severity',
        'Exception records: emergency merges with lifecycle tracking and linked remediation',
      ],
    },
    {
      title: 'Evidence pack structure for auditors',
      paragraphs: [
        'ISO 27001 auditors typically request a sample of changes during the assessment period and ask for evidence that each sampled change was authorized, tested, and approved before deployment. EvidentTrail evidence packs are designed specifically for this workflow.',
        'Each evidence pack includes a machine-readable manifest listing every event in the period, PDF exports suitable for auditor review, CSV exports for auditor analysis, and JSON exports for integration with GRC platforms. Every pack includes SHA-256 checksums for tamper-evident delivery — the auditor can verify that the evidence was not modified after export.',
        'Guest mode allows auditors to access evidence directly without a permanent EvidentTrail account. You generate a scoped evidence pack, share the export link, and the auditor downloads their sample. No GitHub access is granted, and no sensitive configuration is exposed.',
      ],
      bullets: [
        'PDF, CSV, and JSON export formats included in every evidence pack',
        'SHA-256 checksums for tamper-evident delivery to auditors',
        'Guest access for auditor self-serve download — no permanent account required',
        'Configurable date range to scope evidence to the audit period',
      ],
    },
    {
      title: 'Common A.8.32 audit findings and how to avoid them',
      paragraphs: [
        'The most frequent ISO 27001 A.8.32 findings in software development audits fall into three categories: missing approval evidence for sampled changes, no documentation of branch protection configuration changes, and exception records that lack context about what remediation was taken.',
        'Missing approval evidence typically occurs when developers merge via the GitHub web interface in ways that bypass branch protection, or when branch protection was temporarily disabled and then re-enabled without a record of what changed during the gap. EvidentTrail captures drift events that expose exactly these gaps, and flags changes merged during periods of reduced protection.',
        'Exception records that lack context are another common finding. A hotfix merged at 2 AM with no linked ticket and no post-incident review creates an audit question that is difficult to answer months later. EvidentTrail exception tracking records the emergency merge event, links it to the relevant PR, and provides a field for documenting the remediation action taken.',
      ],
      bullets: [
        'Bypassed branch protection: EvidentTrail flags merges during drift windows',
        'Missing approval records: continuous capture ensures no event is missed',
        'Undocumented exceptions: exception lifecycle tracking with remediation fields',
        'Stale evidence: continuous capture throughout the year, not one-time exports',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="ISO 27001 A.8.32 Change Management Evidence from GitHub | EvidentTrail"
      description="Automate ISO 27001 Annex A control A.8.32 change management evidence from GitHub. PR approvals, branch protection snapshots, CI check results — mapped to A.8.32 and exported as audit-ready packs."
      h1="ISO 27001 A.8.32 Change Management Evidence"
      heroText="Map every GitHub pull request, branch protection change, and CI check result to ISO 27001 Annex A control A.8.32 — automatically, continuously, and without write access to your repositories."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Solutions', url: 'https://evidenttrail.com/solutions' },
          { name: 'ISO 27001 A.8.32 Evidence', url: 'https://evidenttrail.com/solutions/iso-27001-a832-evidence' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* A.8.32 Control Mapping */}
            <h2 className={`${tokens.heading.h2} mb-6`}>A.8.32 Requirements Mapped to GitHub Evidence</h2>
            <p className={`${tokens.text.base} mb-8`}>
              ISO 27001:2022 Annex A control A.8.32 requires a formal change management procedure covering authorization, testing, and documentation.
              GitHub pull requests and branch protection rules satisfy each component — EvidentTrail captures the evidence automatically.
            </p>

            <div className="space-y-4 mb-12">
              {[
                {
                  requirement: 'Authorization',
                  standard: 'Changes must be formally authorized before implementation.',
                  evidence: 'Pull request creation records the change request. Branch protection rules enforce minimum reviewer counts before merge is permitted.',
                },
                {
                  requirement: 'Risk & Impact Assessment',
                  standard: 'The impact of the change must be considered before implementation.',
                  evidence: 'Code review activity recorded per reviewer. Required CI checks provide automated risk signals (test failures, security scans).',
                },
                {
                  requirement: 'Testing',
                  standard: 'Changes must be tested before deployment to production.',
                  evidence: 'Required CI status check results captured with pass/fail and timestamp — linked to the specific PR and reviewer.',
                },
                {
                  requirement: 'Approval',
                  standard: 'Changes require explicit approval from an authorized person.',
                  evidence: 'Reviewer identity, approval decision, and timestamp captured per PR. Segregation of duty verified — author cannot self-approve.',
                },
                {
                  requirement: 'Record-Keeping',
                  standard: 'A record of changes and their authorization must be maintained.',
                  evidence: 'Immutable evidence pack with SHA-256 checksum covering the full audit period. PDF, CSV, and JSON formats for auditor consumption.',
                },
                {
                  requirement: 'Exception Handling',
                  standard: 'Emergency changes outside the normal process must be documented.',
                  evidence: 'Exception records capture emergency merges with lifecycle tracking, linked approvals, and remediation documentation.',
                },
              ].map(({ requirement, standard, evidence }) => (
                <div key={requirement} className={tokens.card}>
                  <div className="flex items-center justify-between mb-3 border-b border-slate-700 pb-2">
                    <h3 className="text-white font-bold">{requirement}</h3>
                    <span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded text-xs font-mono">A.8.32</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-2 italic">{standard}</p>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <span className="text-primary-400 text-xs font-medium">EvidentTrail evidence: </span>
                    <span className="text-slate-300 text-sm">{evidence}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* ISO 27001:2022 vs 2013 note */}
            <div className="mb-12 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-400">
                <strong className="text-slate-300">ISO 27001:2022 vs 2013:</strong>{' '}
                Control A.8.32 in the 2022 version corresponds to A.12.1.2 in the 2013 version.
                The evidence requirements are substantially the same. EvidentTrail evidence packs satisfy both versions.
              </p>
            </div>

            {/* Manual vs Automated comparison */}
            <h2 className={`${tokens.heading.h2} mb-6`}>Manual Evidence vs EvidentTrail</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Task</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Manual</th>
                    <th className="text-left py-3 px-4 text-primary-400 font-medium">EvidentTrail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    ['Capture PR approvals', 'Screenshot each PR individually', 'Automatic on every merge'],
                    ['Branch protection history', 'Not available in GitHub', 'Snapshot captured at each change'],
                    ['CI check results', 'Export from CI platform separately', 'Linked to PR evidence automatically'],
                    ['Exception records', 'Manual ticket with no structure', 'Structured lifecycle with linked evidence'],
                    ['Auditor delivery', 'Email zip of screenshots', 'Tamper-evident pack with checksum'],
                    ['Evidence period coverage', 'Ad-hoc, gaps likely', 'Continuous — every event captured'],
                    ['Time per audit cycle', '20–40 hours', 'Minutes to generate export'],
                  ].map(([task, manual, automated]) => (
                    <tr key={task} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-300">{task}</td>
                      <td className="py-3 px-4 text-slate-500">{manual}</td>
                      <td className="py-3 px-4 text-slate-300">{automated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Collecting A.8.32 Evidence
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related ISO 27001 Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/iso-27001-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 GitHub Guide</h4>
              <p className="text-sm text-slate-400">Complete GitHub configuration and evidence guide for ISO 27001 A.8.32 audits.</p>
            </Link>
            <Link to="/iso-27001-vs-soc2" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 vs SOC 2</h4>
              <p className="text-sm text-slate-400">Evidence overlap and how to pursue both frameworks simultaneously.</p>
            </Link>
            <Link to="/soc2-change-management-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 CC8.1</h4>
              <p className="text-sm text-slate-400">The US equivalent — largely the same GitHub evidence applies.</p>
            </Link>
            <Link to="/tools/branch-protection-snapshot" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Branch Protection Snapshot</h4>
              <p className="text-sm text-slate-400">Free tool — export your current branch protection configuration as evidence.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
